/** Generische View-Basis — Pendant zu `Services/Base/APIViewBase.cs`. */

import type { ChinaAppValidationStatus } from '@/types/definitions';
import type { BaseEntity, ChinaAppSysAudit, Document } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';
import { base64ToBytes } from '@/utils/file';

import { buildAppPath } from './apiBase';
import { apiClient, readJson, UserFriendlyError } from './httpClient';

export interface ViewQueryOptions {
  /** Werte kleiner 1 werden auf 200 gesetzt. */
  pageSize?: number;
  /** Werte kleiner 1 werden auf 1 gesetzt. */
  page?: number;
  /** `"Feld asc,Feld2 desc"` */
  orderBy?: string;
  /** `'Feld contains "x" && Feld2 equal "1"'` */
  whereQuery?: string;
}

export interface ViewApi<T extends BaseEntity> {
  readonly endpoint: string;
  readonly entityType: EntityTypes;
  get(viewId: number, options?: ViewQueryOptions): Promise<T[]>;
  getMaxCount(viewId: number, whereQuery?: string): Promise<number>;
  getEntry(viewId: number, id: number): Promise<T>;
  getAudit(viewId: number, id: number): Promise<ChinaAppSysAudit[]>;
  create(viewId: number, model: T): Promise<T>;
  validate(viewId: number, model: T): Promise<ChinaAppValidationStatus>;
  /**
   * Bei HTTP 409 und gesetztem `columnName` wird der DB-Stand geladen, nur diese Spalte
   * aus `model` übernommen und erneut gespeichert (Konfliktauflösung wie im Altclient).
   */
  put(viewId: number, id: number, model: T, columnName?: string): Promise<T>;
  remove(viewId: number, id: number): Promise<boolean>;
  /** Antwort ist ein Base64-JSON-String; liefert die dekodierten Bytes. */
  exportXlsx(viewId: number, options?: ViewQueryOptions): Promise<Uint8Array>;
}

/** Ergebnis von `Clone`/`Divide` — Pendant zum Tupel `(bool success, string message)`. */
export interface MutationResult {
  success: boolean;
  message: string | null;
}

export async function toMutationResult(response: Response): Promise<MutationResult> {
  if (response.ok) {
    return { success: true, message: null };
  }
  return { success: false, message: await response.text() };
}

/**
 * POST mit leerem JSON-Body für die Neuberechnung der Frachtkosten. `errorPrefix` bleibt
 * je Service wörtlich erhalten — der Altclient schreibt einmal „Neukalkulieren“ und einmal
 * „Newkalkulieren“.
 */
export async function postRecalculation<T>(path: string, errorPrefix: string): Promise<T> {
  const response = await apiClient.raw('POST', path, '');
  if (response.ok) {
    return readJson<T>(response);
  }
  throw new Error(`${errorPrefix} Code ${response.status} ${await response.text()}`);
}

/** `GET {base}/{viewId}/{id}/dokumente`; bei Fehlerstatus eine leere Liste. */
export async function fetchDocuments(
  endpoint: string,
  viewId: number,
  id: number,
): Promise<Document[]> {
  const response = await apiClient.raw(
    'GET',
    `${buildAppPath(endpoint)}/${viewId}/${id}/dokumente`,
  );
  if (response.ok) {
    return readJson<Document[]>(response);
  }
  return [];
}

function buildViewQuery(options: ViewQueryOptions = {}): string {
  const pageSize = options.pageSize !== undefined && options.pageSize >= 1 ? options.pageSize : 200;
  const page = options.page !== undefined && options.page >= 1 ? options.page : 1;
  let query = `?pageSize=${pageSize}&page=${page}`;

  const orderBy = encodeURIComponent(options.orderBy ?? '');
  if (orderBy.length > 0) {
    query += `&orderBy=${orderBy}`;
  }
  const whereQuery = encodeURIComponent(options.whereQuery ?? '');
  if (whereQuery.length > 0) {
    query += `&whereQry=${whereQuery}`;
  }
  return query;
}

/**
 * `APIViewBase` verpackt jede Ausnahme, die keine `ChinaAppUserFriendlyException` ist, in eine
 * `AggregateException` mit festem Text. Pendant dazu ist ein `Error` mit `cause`.
 */
async function wrapErrors<TResult>(text: string, action: () => Promise<TResult>): Promise<TResult> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof UserFriendlyError) {
      throw error;
    }
    throw new Error(text, { cause: error });
  }
}

export function createViewApi<T extends BaseEntity>(
  endpoint: string,
  entityType: EntityTypes,
): ViewApi<T> {
  const path = (): string => buildAppPath(endpoint);

  async function getEntry(viewId: number, id: number): Promise<T> {
    return wrapErrors('Fehler in GetEntry', () => apiClient.get<T>(`${path()}/${viewId}/${id}`));
  }

  async function putEntry(viewId: number, id: number, model: T, columnName?: string): Promise<T> {
    const response = await apiClient.raw('PUT', `${path()}/${viewId}/${id}`, model);
    if (response.ok) {
      return readJson<T>(response);
    }

    // Konflikt, aber Spaltenname gegeben => automatisch überschreiben
    if (response.status === 409 && columnName !== undefined && columnName.length > 0) {
      const dbModel = await getEntry(viewId, id);
      const target = dbModel as unknown as Record<string, unknown>;
      target[columnName] = (model as unknown as Record<string, unknown>)[columnName];
      return putEntry(viewId, id, dbModel, columnName);
    }

    throw new UserFriendlyError(
      'Datensatz konnte nicht aktualisiert werden.',
      await response.text(),
    );
  }

  async function post<TResult>(url: string, model: T, caption: string): Promise<TResult> {
    const response = await apiClient.raw('POST', url, model);
    if (response.ok) {
      return readJson<TResult>(response);
    }
    throw new UserFriendlyError(caption, `Code: ${response.status} ${await response.text()}`);
  }

  return {
    endpoint,
    entityType,

    get: (viewId, options) =>
      wrapErrors('Fehler in Get', () =>
        apiClient.get<T[]>(`${path()}/${viewId}${buildViewQuery(options)}`),
      ),

    getMaxCount: (viewId, whereQuery) =>
      wrapErrors('Fehler in GetMaxCount', () => {
        const where = encodeURIComponent(whereQuery ?? '');
        const suffix = where.length > 0 ? `?whereQry=${where}` : '';
        return apiClient.get<number>(`${path()}/${viewId}/maxcount${suffix}`);
      }),

    getEntry,

    getAudit: (viewId, id) =>
      wrapErrors('Fehler in GetAudit', () =>
        apiClient.get<ChinaAppSysAudit[]>(`${path()}/${viewId}/${id}/audit`),
      ),

    create: (viewId, model) =>
      wrapErrors('Fehler in Create', () =>
        post<T>(`${path()}/${viewId}`, model, 'Datensatz konnte nicht angelegt werden.'),
      ),

    // Der Altclient meldet auch bei der Prüfung „konnte nicht angelegt werden“.
    validate: (viewId, model) =>
      wrapErrors('Fehler in Create', () =>
        post<ChinaAppValidationStatus>(
          `${path()}/${viewId}/validate`,
          model,
          'Datensatz konnte nicht angelegt werden.',
        ),
      ),

    put: (viewId, id, model, columnName) =>
      wrapErrors('Fehler in Put', () => putEntry(viewId, id, model, columnName)),

    remove: (viewId, id) =>
      wrapErrors('Fehler in Delete', async () => {
        const response = await apiClient.raw('DELETE', `${path()}/${viewId}/${id}`);
        return response.ok;
      }),

    exportXlsx: (viewId, options) =>
      wrapErrors('Fehler in Get', async () => {
        const base64 = await apiClient.get<string>(
          `${path()}/${viewId}/export/xlsx${buildViewQuery(options)}`,
        );
        return base64ToBytes(base64);
      }),
  };
}
